import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import Layout from "../../components/Layout";
import api, { fmtErr, fmtMoney, toastErr } from "../../api";
import { toast } from "sonner";
import { TermsPanel } from "./QuoteTermsPanel";
import { fileToDataUrl, ReviseButton } from "./QuoteAcceptance";
import { QuoteActions } from "./QuoteActions";

const inp = "field-input mt-1 w-full";
const L = ({ label, children, testid }) => (
  <label className="block text-xs font-bold" data-testid={testid}>{label}{children}</label>
);
const TAXES_ON = "Prices in Canadian dollars; taxes shown above.";
const TAXES_OFF = "Prices in Canadian dollars.";
const TAXES_SEASONAL = "Prices in Canadian dollars; applicable taxes extra.";

const defaultTerms = (lib, showTax) =>
  [...(lib.standard || []), ...(lib.custom || [])]
    .filter((t) => ["both", "one_time"].includes(t.applies_to || "both"))
    .map((t) => ({
      term_id: t.id,
      text: t.id === "std_taxes" ? (showTax ? TAXES_ON : TAXES_OFF) : t.text,
      on: !!t.default_on,
      kind: (lib.standard || []).some((s) => s.id === t.id) ? "standard" : "custom",
    }));

export function QuoteOneTime({ editId }) {
  const params = useParams();
  const id = editId || params.id;
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const [jobDate, setJobDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [converting, setConverting] = useState(false);
  const [signer, setSigner] = useState("");
  const [po, setPo] = useState("");
  const [scan, setScan] = useState("");
  const [q, setQ] = useState(null);
  const [leads, setLeads] = useState([]);
  const [owners, setOwners] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [termsLib, setTermsLib] = useState({ standard: [], custom: [] });
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);
  const [kind, setKind] = useState(sp.get("owner") ? "owner" : "lead");
  const [leadId, setLeadId] = useState(sp.get("lead") || "");
  const [ownerId, setOwnerId] = useState(sp.get("owner") || "");
  const [newLead, setNewLead] = useState({ name: "", company: "", email: "", phone: "", address: "",
    billing_name: "", billing_company: "", billing_email: "", billing_cc_email: "", billing_phone: "",
    billing_address: "", billing_same_as_service: true, site_contact_name: "", site_contact_phone: "" });
  const [title, setTitle] = useState("");
  const [bullets, setBullets] = useState([""]);
  const [lines, setLines] = useState([{ description: "", qty: "", amount: "", _k: crypto.randomUUID() }]);
  const [showTax, setShowTax] = useState(true);
  const [timing, setTiming] = useState("");
  const [validUntil, setValidUntil] = useState(() => new Date(Date.now() + 30 * 864e5).toISOString().slice(0, 10));
  const [terms, setTerms] = useState([]);
  const [templateId, setTemplateId] = useState("");

  useEffect(() => {
    api.get("/leads").then((r) => setLeads(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    api.get("/owners").then((r) => setOwners(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    api.get("/quote-templates").then((r) => setTemplates((r.data || []).filter((t) => t.quote_type === "one_time"))).catch(() => {});
    api.get("/quote-terms").then((r) => setTermsLib(r.data)).catch(() => {});
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- default terms seeded once per new quote
  useEffect(() => { if (!q && (termsLib.standard || []).length) setTerms(defaultTerms(termsLib, true)); }, [termsLib]);

  const repopulate = (d) => {
    setQ(d);
    setKind(d.owner_id ? "owner" : "lead");
    setLeadId(d.lead_id || "");
    setOwnerId(d.owner_id || "");
    setTitle(d.title || "");
    setBullets((d.visit_inclusions || []).length ? d.visit_inclusions : [""]);
    setLines(((d.price_lines || []).length ? d.price_lines : [{ description: "", qty: "", amount: "" }])
      .map((l) => ({ ...l, qty: l.qty ?? "", amount: l.amount ?? "", _k: crypto.randomUUID() })));
    setShowTax(d.show_tax !== false);
    setTiming(d.target_timing || "");
    setValidUntil(d.valid_until || "");
    setTerms(d.terms || []);
    setTemplateId(d.template_id || "");
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only load for an existing draft
  useEffect(() => {
    if (!id) return;
    api.get(`/quotes/${id}`).then((r) => repopulate(r.data)).catch((e) => toastErr(e));
  }, [id]);

  const applyTemplate = async (tid) => {
    setTemplateId(tid);
    const t = templates.find((x) => x.id === tid);
    if (!t) return;
    if (q) {
      try {
        const r = await api.patch(`/quotes/${q.id}`, { template_id: tid });
        repopulate(r.data);
        toast.success("Template applied — wording and terms replaced");
      } catch (e) { toastErr(e); }
      return;
    }
    setTitle(t.title || "");
    setBullets((t.visit_inclusions || []).length ? t.visit_inclusions : [""]);
    if (t.defaults?.price != null) {
      setLines([{ description: t.title || "", qty: "", amount: String(t.defaults.price), _k: crypto.randomUUID() }]);
    } else {
      setLines((ls) => ls.map((l, i) => (i === 0 && !l.description ? { ...l, description: t.title || "" } : l)));
    }
    if ((t.default_terms || []).length) {
      setTerms(defaultTerms(termsLib, showTax).map((x) => ({ ...x, on: t.default_terms.includes(x.term_id) })));
    }
  };

  const subtotal = lines.reduce((s2, l) => s2 + (Number(l.amount) || 0) * (Number(l.qty) || 1), 0);
  const taxOn = showTax && settings?.tax_enabled;
  const tax = taxOn ? Math.round(subtotal * (Number(settings?.tax_rate ?? 13) / 100) * 100) / 100 : 0;
  const total = Math.round((subtotal + tax) * 100) / 100;

  const toggleTax = (v) => {
    setShowTax(v);
    setTerms((ts) => ts.map((t) => (t.term_id === "std_taxes" && [TAXES_ON, TAXES_OFF, TAXES_SEASONAL].includes(t.text)
      ? { ...t, text: v ? TAXES_ON : TAXES_OFF } : t)));
    if (q) api.patch(`/quotes/${q.id}`, { show_tax: v }).then((r) => setQ(r.data)).catch((e) => toastErr(e));
  };

  const onTermsChange = (t) => {
    setTerms(t);
    if (q) api.patch(`/quotes/${q.id}`, { terms: t }).then((r) => setQ(r.data)).catch((e) => toastErr(e));
  };

  const payload = () => ({
    quote_type: "one_time",
    lead_id: kind === "lead" && leadId ? leadId : null,
    owner_id: kind === "owner" && ownerId ? ownerId : null,
    new_lead: !q && kind === "new" ? newLead : null,
    template_id: templateId || null,
    title, visit_inclusions: bullets.filter((b) => b.trim()),
    price_lines: lines.filter((l) => l.description.trim() || l.amount !== "")
      .map((l) => ({ description: l.description.trim(), qty: l.qty === "" ? null : Number(l.qty), amount: Number(l.amount) || 0 })),
    show_tax: showTax, target_timing: timing, valid_until: validUntil || null, terms,
  });

  const save = async () => {
    if (!q && kind !== "new" && !leadId && !ownerId) { toast.error("Pick a lead or an owner first"); return; }
    if (!title.trim()) { toast.error("Give the job a title"); return; }
    setBusy(true);
    try {
      if (!q) {
        const r = await api.post("/quotes", payload());
        setQ(r.data);
        window.history.replaceState(null, "", `/quotes/${r.data.id}`);
        toast.success(`Draft ${r.data.quote_number} saved — ready to send`);
      } else {
        const r = await api.patch(`/quotes/${q.id}`, payload());
        setQ(r.data);
        toast.success("Saved");
      }
    } catch (e) { toastErr(e); }
    setBusy(false);
  };

  const setLine = (i, k, v) => setLines((ls) => ls.map((l, j) => (j === i ? { ...l, [k]: v } : l)));

  const convertJob = async () => {
    setConverting(true);
    try {
      const r = await api.post(`/quotes/${q.id}/convert-to-job`,
        { job_date: jobDate, signer_name: signer, po_number: po, signed_scan: scan });
      toast.success(r.data.already ? "Already converted — opening the job" : `Job scheduled for ${jobDate} — quote marked accepted`);
      setQ((old) => ({ ...old, status: "accepted", converted_visit_id: r.data.visit_id }));
      nav(`/visits/${r.data.visit_id}`);
    } catch (e) { toastErr(e); }
    setConverting(false);
  };

  return (
    <Layout title={q ? `Quote ${q.quote_number}` : "One-time job quote"}>
      <div className="space-y-4" data-testid="one-time-form">
        {!q ? (
          <div className="space-y-3" data-testid="ot-customer">
            <div className="flex gap-2">
              {[["lead", "Existing lead"], ["owner", "Existing owner"], ["new", "New lead"]].map(([k, lbl]) => (
                <button key={k} data-testid={`ot-kind-${k}`} onClick={() => setKind(k)}
                  className={`flex-1 rounded-md border-2 px-2 py-2 text-xs font-bold ${kind === k ? "border-primary bg-primary/10" : "border-border"}`}>{lbl}</button>
              ))}
            </div>
            {kind === "lead" && (
              <L label="Lead" testid="ot-lbl-lead"><select data-testid="ot-pick-lead" className={inp} value={leadId} onChange={(e) => setLeadId(e.target.value)}>
                <option value="">Choose a lead…</option>
                {leads.map((l) => <option key={l.id} value={l.id}>{l.name}{l.company ? ` — ${l.company}` : ""}</option>)}
              </select></L>
            )}
            {kind === "owner" && (
              <L label="Owner" testid="ot-lbl-owner"><select data-testid="ot-pick-owner" className={inp} value={ownerId} onChange={(e) => setOwnerId(e.target.value)}>
                <option value="">Choose an owner…</option>
                {owners.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select></L>
            )}
            {kind === "new" && (
              <div className="space-y-2" data-testid="ot-new-lead-form">
                {[["name", "Name"], ["company", "Company (optional)"], ["email", "Email"], ["phone", "Phone"], ["address", "Service address"]].map(([k, lbl]) => (
                  <L key={k} label={lbl} testid={`ot-lbl-new-${k}`}>
                    <input data-testid={`ot-new-${k}`} className={inp} value={newLead[k]}
                      onChange={(e) => setNewLead({ ...newLead, [k]: e.target.value })} />
                  </L>
                ))}
                <p className="pt-1 text-xs font-bold text-muted-foreground">Billing contact (if different)</p>
                {[["billing_name", "Billing contact name"], ["billing_company", "Company / org (optional)"], ["billing_email", "Billing email"], ["billing_phone", "Billing phone"]].map(([k, lbl]) => (
                  <L key={k} label={lbl} testid={`ot-lbl-new-${k}`}>
                    <input data-testid={`ot-new-${k}`} className={inp} value={newLead[k]}
                      onChange={(e) => setNewLead({ ...newLead, [k]: e.target.value })} />
                  </L>
                ))}
                <label className="flex items-center gap-2 text-xs font-semibold">
                  <input type="checkbox" data-testid="ot-new-billing-same" checked={newLead.billing_same_as_service}
                    onChange={(e) => setNewLead({ ...newLead, billing_same_as_service: e.target.checked })} />
                  Billing address same as service address
                </label>
                {!newLead.billing_same_as_service && (
                  <L label="Billing address" testid="ot-lbl-new-billing-address">
                    <input data-testid="ot-new-billing_address" className={inp} value={newLead.billing_address}
                      onChange={(e) => setNewLead({ ...newLead, billing_address: e.target.value })} />
                  </L>
                )}
                <p className="pt-1 text-xs font-bold text-muted-foreground">Site contact at the property (optional)</p>
                <div className="flex gap-2">
                  <input data-testid="ot-new-site_contact_name" className={inp} placeholder="Name" value={newLead.site_contact_name}
                    onChange={(e) => setNewLead({ ...newLead, site_contact_name: e.target.value })} />
                  <input data-testid="ot-new-site_contact_phone" className={inp} placeholder="Phone" value={newLead.site_contact_phone}
                    onChange={(e) => setNewLead({ ...newLead, site_contact_phone: e.target.value })} />
                </div>
              </div>
            )}
          </div>
        ) : (
          <p className="card-flat p-3 text-sm" data-testid="ot-recipient">
            For <strong>{q.contact_name}</strong>{q.company_name ? ` (${q.company_name})` : ""}
            {q.email ? ` · ${q.email}` : ""}{q.service_address ? ` · ${q.service_address}` : ""}
          </p>
        )}

        <L label="Template (optional)" testid="ot-lbl-template">
          <select data-testid="ot-template" className={inp} value={templateId} onChange={(e) => applyTemplate(e.target.value)}>
            <option value="">No template</option>
            {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </L>

        <L label="Job title" testid="ot-lbl-title">
          <input data-testid="ot-title" className={inp} value={title} placeholder='e.g. "Fall leaf cleanup"'
            onChange={(e) => setTitle(e.target.value)} />
        </L>

        <div>
          <p className="text-xs font-bold">Description</p>
          {bullets.map((b, i) => (
            <div key={i} className="mb-1 flex items-center gap-1">
              <input className="field-input flex-1" value={b} data-testid={`ot-bullet-${i}`}
                placeholder="One bullet per line of work"
                onChange={(e) => setBullets(bullets.map((x, j) => (j === i ? e.target.value : x)))} />
              {bullets.length > 1 && (
                <button data-testid={`ot-bullet-del-${i}`} className="px-1 text-xs font-bold text-red-600"
                  onClick={() => setBullets(bullets.filter((_, j) => j !== i))}>✕</button>
              )}
            </div>
          ))}
          <button data-testid="ot-add-bullet" className="text-xs font-bold text-primary"
            onClick={() => setBullets([...bullets, ""])}>+ Add bullet</button>
        </div>

        <div>
          <p className="text-xs font-bold">Price</p>
          {lines.map((l, i) => (
            <div key={l._k} className="mb-2 flex items-center gap-1" data-testid={`ot-line-${i}`}>
              <input className="field-input flex-1" value={l.description} placeholder="Description" data-testid={`ot-line-desc-${i}`}
                onChange={(e) => setLine(i, "description", e.target.value)} />
              <input className="field-input w-16" value={l.qty} type="number" min="1" placeholder="Qty" data-testid={`ot-line-qty-${i}`}
                onChange={(e) => setLine(i, "qty", e.target.value)} />
              <input className="field-input w-24" value={l.amount} type="number" min="0" placeholder="$" data-testid={`ot-line-amount-${i}`}
                onChange={(e) => setLine(i, "amount", e.target.value)} />
              {lines.length > 1 && (
                <button data-testid={`ot-line-del-${i}`} className="px-1 text-xs font-bold text-red-600"
                  onClick={() => setLines(lines.filter((_, j) => j !== i))}>✕</button>
              )}
            </div>
          ))}
          <button data-testid="ot-add-line" className="text-xs font-bold text-primary"
            onClick={() => setLines([...lines, { description: "", qty: "", amount: "", _k: crypto.randomUUID() }])}>+ Add line</button>
        </div>

        <div className="card-flat space-y-2 p-4" data-testid="ot-totals">
          <label className="flex items-center gap-2 text-xs font-bold">
            <input type="checkbox" data-testid="ot-show-tax" checked={showTax} onChange={(e) => toggleTax(e.target.checked)} />
            Show tax breakdown on the quote
          </label>
          <div className="flex justify-between text-sm"><span className="text-muted-foreground">Subtotal</span><span data-testid="ot-subtotal">{fmtMoney(subtotal)}</span></div>
          {taxOn && (
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">{settings?.tax_label || "Tax"} ({settings?.tax_rate}%)</span>
              <span data-testid="ot-tax">{fmtMoney(tax)}</span>
            </div>
          )}
          <div className="flex justify-between text-base font-bold"><span>Total</span><span data-testid="ot-total">{fmtMoney(total)}</span></div>
          {showTax && settings && !settings.tax_enabled && (
            <p className="text-xs text-muted-foreground" data-testid="ot-tax-off-note">Tax isn't enabled in your Settings — only the subtotal shows.</p>
          )}
        </div>

        <L label="Target timing (optional)" testid="ot-lbl-timing">
          <input data-testid="ot-timing" className={inp} value={timing} placeholder='e.g. "Week of Oct 27, weather permitting"'
            onChange={(e) => setTiming(e.target.value)} />
        </L>
        <L label="Valid until" testid="ot-lbl-valid">
          <input data-testid="ot-valid-until" type="date" className={inp} value={validUntil} onChange={(e) => setValidUntil(e.target.value)} />
        </L>

        <TermsPanel terms={terms} termsLib={termsLib} onChange={onTermsChange}
          onLibChange={setTermsLib} quoteType="one_time" />

        <button data-testid="ot-save" onClick={save} disabled={busy}
          className="h-12 w-full rounded-md bg-primary text-sm font-bold text-primary-foreground disabled:opacity-50">
          {busy ? "Saving…" : q ? "Save changes" : "Save draft"}
        </button>

        {q && (
          <div className="space-y-2">
            <QuoteActions q={q} onChanged={setQ} />
            {["sent", "viewed", "declined", "expired", "accepted"].includes(q.status) && !q.converted_visit_id && <ReviseButton q={q} />}
            <p className="text-xs text-muted-foreground" data-testid="ot-number-line">Number: {q.quote_number} · status: {q.status}</p>
          </div>
        )}

        {q && !q.converted_visit_id && q.status !== "superseded" && (
          <div className="card-flat space-y-2 p-4" data-testid="ot-convert-block">
            <p className="text-sm font-bold" data-testid="ot-convert-heading">
              {q.status === "accepted"
                ? `Accepted${q.accepted_name ? ` by ${q.accepted_name}` : ""} — schedule the job`
                : "Customer approved?"}
            </p>
            {q.status !== "accepted" && (
              <>
                <p className="text-xs text-muted-foreground">
                  Turns this quote into a job on your schedule. If it went to a lead, they're converted to a
                  customer first (contact only — no portal invite). Completing the job drafts the invoice at the
                  quoted price.
                </p>
                <input data-testid="ot-signer" className="field-input w-full" placeholder="Signer name (who approved)"
                  value={signer} onChange={(e) => setSigner(e.target.value)} />
                <div className="flex items-center gap-2">
                  <input data-testid="ot-po" className="field-input flex-1" placeholder="PO number (optional)"
                    value={po} onChange={(e) => setPo(e.target.value)} />
                  <label className="shrink-0 text-xs font-bold text-primary underline">
                    {scan ? "Scan attached ✓" : "Signed scan"}
                    <input data-testid="ot-scan" type="file" accept="image/*" className="hidden"
                      onChange={async (e) => setScan(e.target.files?.[0] ? await fileToDataUrl(e.target.files[0]) : "")} />
                  </label>
                </div>
              </>
            )}
            <div className="flex gap-2">
              <input type="date" data-testid="ot-convert-date" className="field-input" value={jobDate}
                onChange={(e) => setJobDate(e.target.value)} />
              <button data-testid="ot-convert-job" onClick={convertJob} disabled={converting}
                className="h-12 flex-1 rounded-md bg-primary text-sm font-bold text-primary-foreground disabled:opacity-50">
                {converting ? "Converting…" : q.status === "accepted" ? "Schedule job" : "Convert to job"}
              </button>
            </div>
          </div>
        )}
        {q?.status === "accepted" && q?.converted_visit_id && (
          <Link to={`/visits/${q.converted_visit_id}`} data-testid="ot-open-job"
            className="card-flat flex items-center justify-between p-4 font-semibold text-primary">
            Scheduled — open the job <span>→</span>
          </Link>
        )}
      </div>
    </Layout>
  );
}
